import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { IBrowserConversation } from 'core/hooks';

import * as S from './Communicator.styled';

interface IGroupMailListProps {
  threads: IBrowserConversation[];
  loading: boolean;
  onOpen: (thread: IBrowserConversation) => void;
}

/**
 * Whole-group Почта tab: every student's gmail thread in one list (fed by the
 * page's `useGroupCommsIndex` sweep — no query of its own). A row click jumps
 * into that student's mail context with the thread preselected.
 */
export const GroupMailList = memo(
  ({ threads, loading, onOpen }: IGroupMailListProps) => {
    const { t } = useTranslation();

    if (!loading && threads.length === 0) {
      return (
        <S.Card>
          <S.Muted>{t('pages.communicator.mailEmpty')}</S.Muted>
        </S.Card>
      );
    }

    return (
      <S.Card>
        {threads.map((thread) => (
          <S.MailRow
            key={thread.id}
            type="button"
            onClick={() => onOpen(thread)}
          >
            <S.RowMain>
              <b>
                {thread.student
                  ? `${thread.student.lastName} ${thread.student.firstName}`
                  : thread.displayTitle}
              </b>
              <span>
                {thread.displayTitle}
                {thread.mailboxEmail ? ` · ${thread.mailboxEmail}` : ''}
              </span>
            </S.RowMain>
            {thread.isUnanswered && <S.UnansweredDot />}
            {thread.unreadCount > 0 && (
              <S.UnreadBubble>{thread.unreadCount}</S.UnreadBubble>
            )}
            {thread.lastMessageAt ? (
              <S.MailTime>
                {moment(thread.lastMessageAt).format('DD.MM HH:mm')}
              </S.MailTime>
            ) : null}
          </S.MailRow>
        ))}
      </S.Card>
    );
  },
);

GroupMailList.displayName = 'GroupMailList';
